import { DEFAULT_LOCALE, LOCALE_CONFIG, LOCALES, normalizeLocale, type Locale } from "./config";

export function localePath(locale: Locale, pathname: string): string {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const prefix = LOCALE_CONFIG[locale].pathPrefix;
  if (!prefix) return normalized;
  if (normalized === "/") return prefix;
  if (normalized === prefix || normalized.startsWith(`${prefix}/`)) return normalized;
  return `${prefix}${normalized}`;
}

export function localeFromPathname(pathname: string | null | undefined): Locale {
  const normalized = pathname?.startsWith("/") ? pathname : `/${pathname ?? ""}`;
  for (const locale of LOCALES) {
    const prefix = LOCALE_CONFIG[locale].pathPrefix;
    if (!prefix) continue;
    if (normalized === prefix || normalized.startsWith(`${prefix}/`)) return locale;
  }
  return DEFAULT_LOCALE;
}

export function stripLocalePrefix(pathname: string | null | undefined): string {
  const normalized = pathname?.startsWith("/") ? pathname : `/${pathname ?? ""}`;
  for (const locale of LOCALES) {
    const prefix = LOCALE_CONFIG[locale].pathPrefix;
    if (!prefix) continue;
    if (normalized === prefix) return "/";
    if (normalized.startsWith(`${prefix}/`)) return normalized.slice(prefix.length) || "/";
  }
  return normalized || "/";
}

export function switchLocaleHref(
  currentLocale: Locale,
  pathname: string,
  search?: string | null,
  targetLocale?: Locale,
): string {
  const locale = targetLocale ?? LOCALES.find((candidate) => candidate !== currentLocale) ?? DEFAULT_LOCALE;
  const path = localePath(locale, stripLocalePrefix(pathname || "/"));

  if (!search) return path;
  const params = new URLSearchParams(search);
  params.delete("locale");
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function localizedAlternates(pathname: string): Record<string, string> {
  const normalized = stripLocalePrefix(pathname);
  return Object.fromEntries(LOCALES.map((locale) => [LOCALE_CONFIG[locale].hrefLang, localePath(locale, normalized)]));
}

export function appHref(path: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return path;
  const url = new URL(path, "https://machuping.local");
  url.searchParams.set("locale", locale);
  return `${url.pathname}${url.search}`;
}

export function localizedContactPath(locale: Locale, query?: URLSearchParams): string {
  const path = localePath(locale, "/contact");
  const qs = query?.toString();
  return qs ? `${path}?${qs}` : path;
}

export function localeFromSearchParams(
  searchParams: Pick<URLSearchParams, "get"> | null | undefined,
  fallbackPath?: string | null,
): Locale {
  const direct = normalizeLocale(searchParams?.get("locale"));
  if (direct !== DEFAULT_LOCALE) return direct;

  const next = fallbackPath?.trim();
  if (next) {
    try {
      const parsed = new URL(next, "https://machuping.local");
      const fromNext = normalizeLocale(parsed.searchParams.get("locale"));
      if (fromNext !== DEFAULT_LOCALE) return fromNext;
      return localeFromPathname(parsed.pathname);
    } catch {
      return DEFAULT_LOCALE;
    }
  }

  return DEFAULT_LOCALE;
}
